import { useSelector } from "react-redux";

import { UserDto } from "../../DTOs/User/UserDto";
import { RootState } from "../../state/store";
import { AreaKey } from "./pageVocabulary.generated";
import { WriteKey } from "./writeVocabulary.generated";
import { useRouteArea } from "./routeArea";

/**
 * Every authorisation predicate below is "Super Admin, or the specific grant". Routing them all
 * through here keeps that bypass identical across them.
 */
const authorised = (user: UserDto | null, granted: (user: UserDto) => boolean): boolean =>
  !!user && (user.isSuperAdmin || granted(user));

export const hasAdminRights = (user: UserDto | null): boolean => !!user && user.isAdmin;

export const hasArea = (user: UserDto | null, areaKey: AreaKey): boolean =>
  authorised(user, u => u.areaKeys.includes(areaKey));

export const hasTile = (user: UserDto | null, tileKey: string): boolean =>
  authorised(user, u => (u.visibleTileKeys ?? []).includes(tileKey));

export const hasLeadAdminRights = (user: UserDto | null): boolean => !!user && user.hasLeadAdminRights;

export const roleName = (user: UserDto | null): string | null => user?.roles[0]?.name ?? null;

export const canEditArea = (user: UserDto | null, area: AreaKey | undefined): boolean =>
  !!area && authorised(user, u => u.editableAreas.includes(area));

export const canWrite = (user: UserDto | null, write: WriteKey): boolean =>
  authorised(user, u => u.allowedWrites.includes(write));

export const isViewOnlyPage = (user: UserDto | null, editControlAreas: readonly AreaKey[]): boolean =>
  !!user && editControlAreas.length > 0 && !editControlAreas.some(area => canEditArea(user, area));

/** Pass an area for a control that changes another page's records, such as stoppages logged from Operations. */
export function useIsViewOnly(area?: AreaKey): boolean {
  const user = useSelector((s: RootState) => s.authReducer.user);
  const pageArea = useRouteArea();

  return !canEditArea(user, area ?? pageArea);
}

/** Nothing on the page may be changed: the shift is locked, or the page is view only to this user. */
export function useIsReadOnly(area?: AreaKey): boolean {
  const isLocked = useSelector((s: RootState) => s.shift.isLocked);
  const isViewOnly = useIsViewOnly(area);

  return isLocked || isViewOnly;
}

export const useSeesCosts = (): boolean => useSelector((s: RootState) => s.authReducer.user?.seesCosts ?? false);

/** For a control whose write the server may refuse to a user who can still edit the rest of the page. */
export function useCanWrite(write: WriteKey): boolean {
  const user = useSelector((s: RootState) => s.authReducer.user);

  return canWrite(user, write);
}
